import { useEffect, useRef } from 'react';

export type HotkeyMatcher = string | RegExp;

export interface HotkeyOptions {
  enabled?: boolean;
  preventDefault?: boolean;
  allowInInputs?: boolean;
}

const isEditable = (el: EventTarget | null): boolean => {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if ((el as HTMLElement).isContentEditable) return true;
  return false;
};

const keyForEvent = (e: KeyboardEvent): string => {
  const parts: string[] = [];
  if (e.ctrlKey || e.metaKey) parts.push('mod');
  if (e.altKey) parts.push('alt');
  if (e.shiftKey) parts.push('shift');
  const k = e.key.length === 1 ? e.key.toLowerCase() : e.key;
  parts.push(k);
  return parts.join('+');
};

export function useHotkey(
  combo: HotkeyMatcher,
  handler: (e: KeyboardEvent) => void,
  opts: HotkeyOptions = {}
) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (opts.enabled === false) return;
    const onKey = (e: KeyboardEvent) => {
      if (!opts.allowInInputs && isEditable(e.target)) return;
      const k = keyForEvent(e);
      const match = typeof combo === 'string' ? k === combo : combo.test(k);
      if (!match) return;
      if (opts.preventDefault) e.preventDefault();
      handlerRef.current(e);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [combo, opts.enabled, opts.preventDefault, opts.allowInInputs]);
}

export type SequenceMap = Record<string, () => void>;

export function useHotkeySequence(
  prefix: string,
  map: SequenceMap,
  opts: HotkeyOptions = {}
) {
  const mapRef = useRef(map);
  mapRef.current = map;

  useEffect(() => {
    if (opts.enabled === false) return;
    let waiting = false;
    let timer: any = null;

    const resetWait = () => {
      waiting = false;
      if (timer) { clearTimeout(timer); timer = null; }
    };

    const onKey = (e: KeyboardEvent) => {
      if (!opts.allowInInputs && isEditable(e.target)) return;
      const k = keyForEvent(e);
      if (!waiting && k === prefix) {
        waiting = true;
        timer = setTimeout(resetWait, 1500);
        if (opts.preventDefault) e.preventDefault();
        return;
      }
      if (waiting) {
        const fn = mapRef.current[k];
        if (fn) {
          if (opts.preventDefault) e.preventDefault();
          fn();
        }
        resetWait();
      }
    };

    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      resetWait();
    };
  }, [prefix, opts.enabled, opts.preventDefault, opts.allowInInputs]);
}
